import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

class EditTrackingService {
    constructor() {
        this.currentSession = null;
        this.edits = [];
        this.isTracking = false;
        this.startRequestId = 0;
        this.lastActivityTime = Date.now();
        this.continuousTrackingInterval = null; // Add this to track continuous time
        this.idleTimeoutMs = 60000;
        this.activityEvents = ['mousedown', 'keydown', 'mousemove', 'wheel', 'touchstart', 'click'];
        this.activityListener = null;
    }

    async startSession(videoId, language, userLevel, editorVersion) {
        const requestId = ++this.startRequestId;

        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));

            if (!authUser || !authToken) {
                console.error('User not authenticated');
                return null;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/start`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    userId: authUser.id || authUser._id,
                    email: authUser.email,
                    videoId,
                    language,
                    userLevel,
                    editorVersion,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to start edit session');
            }

            const startedSession = await response.json();

            if (requestId !== this.startRequestId) {
                await this.endSessionById(startedSession.id);
                return null;
            }

            this.currentSession = startedSession;
            this.isTracking = true;
            this.edits = [];
            this.lastActivityTime = Date.now();

            // Set up continuous tracking with a heartbeat
            if (this.continuousTrackingInterval) {
                clearInterval(this.continuousTrackingInterval);
            }

            // Send a heartbeat every 30 seconds to keep the session alive and record time
            this.continuousTrackingInterval = setInterval(() => {
                const now = Date.now();
                if (now - this.lastActivityTime <= this.idleTimeoutMs) {
                    this.sendHeartbeat();
                }
            }, 30000); // 30-second interval

            // Set up activity tracking
            this.setupActivityTracking();

            console.log('Edit session started:', this.currentSession);
            return this.currentSession;
        } catch (error) {
            console.error('Error starting edit session:', error);
            // need to handle this error if more than 3 consecutive errors occur stop the session
            this.cleanupActivityTracking();

            return null;
        }
    }

    async endSessionById(sessionId) {
        if (!sessionId) {
            return null;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${sessionId}/end`,
                {
                    method: 'POST',
                    headers: {
                        ...getAuthHeaders(true),
                    },
                },
            );

            if (!response.ok && response.status !== 404) {
                throw new Error('Failed to end edit session');
            }

            return response.ok ? response.json() : null;
        } catch (error) {
            console.error('Error ending edit session by id:', error);
            return null;
        }
    }

    setupActivityTracking() {
        if (this.activityListener) {
            return;
        }

        this.activityListener = () => {
            const now = Date.now();
            const wasIdle = now - this.lastActivityTime > this.idleTimeoutMs;
            this.lastActivityTime = now;

            // User resumed activity after idle period: sync active time immediately.
            if (wasIdle && this.currentSession && this.isTracking) {
                this.sendHeartbeat();
            }
        };

        this.activityEvents.forEach((event) => {
            document.addEventListener(event, this.activityListener);
        });
    }

    cleanupActivityTracking() {
        if (!this.activityListener) {
            return;
        }

        this.activityEvents.forEach((event) => {
            document.removeEventListener(event, this.activityListener);
        });
        this.activityListener = null;
    }

    stopTrackingLocally(reason = 'Stopping edit tracking', sessionId = null) {
        if (sessionId && this.currentSession?.id && this.currentSession.id !== sessionId) {
            return;
        }

        console.warn(reason);

        if (this.continuousTrackingInterval) {
            clearInterval(this.continuousTrackingInterval);
            this.continuousTrackingInterval = null;
        }

        this.cleanupActivityTracking();
        this.isTracking = false;
        this.currentSession = null;
    }

    async endSession() {
        if (!this.currentSession || !this.isTracking) {
            console.warn('No active edit session to end');
            return null;
        }

        const sessionId = this.currentSession.id;

        // Clear the continuous tracking interval
        if (this.continuousTrackingInterval) {
            clearInterval(this.continuousTrackingInterval);
            this.continuousTrackingInterval = null;
        }
        this.cleanupActivityTracking();

        try {
            const authToken = JSON.parse(localStorage.getItem('authToken'));

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${sessionId}/end`,
                {
                    method: 'POST',
                    headers: {
                        ...getAuthHeaders(true),
                    },
                },
            );

            if (!response.ok && response.status !== 404) {
                throw new Error('Failed to end edit session');
            }

            const endedSession = response.ok ? await response.json() : null;
            this.stopTrackingLocally('Edit session ended', sessionId);

            console.log('Edit session ended:', endedSession);
            return endedSession;
        } catch (error) {
            console.error('Error ending edit session:', error);
            return null;
        }
    }

    async trackEdit(index, editType, field, oldValue, newValue) {
        if (!this.currentSession || !this.isTracking) {
            console.warn('No active edit session for tracking edits');
            return;
        }

        try {
            const authToken = JSON.parse(localStorage.getItem('authToken'));

            // Store edit locally
            this.edits.push({
                index,
                editType,
                field,
                oldValue,
                newValue,
                timestamp: new Date().toISOString(),
            });

            // Create the request body
            const requestBody = {
                index,
                editType,
                field,
                oldValue,
                newValue,
            };

            // Send to server
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${this.currentSession.id}/edits`;
            const headers = getAuthHeaders(true);
            headers['Content-Type'] = 'application/json';
            const response = await axios.post(url, requestBody, { headers });
        } catch (error) {
            console.error('Error tracking edit:', error);
        }
    }

    async getAvailableFolders() {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/folders`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });

            if (!response.ok) {
                throw new Error('Failed to fetch available folders');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching available folders:', error);
            return [];
        }
    }

    async getUserStats(folderPath = null, videoId = null, dateRange = 'last30days') {
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const userId = authUser.email;
            const params = new URLSearchParams();
            if (folderPath && folderPath !== '/') params.set('folderPath', folderPath);
            if (videoId) params.set('videoId', videoId);
            if (dateRange) params.set('dateRange', dateRange);
            const query = params.toString();
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/user/stats${query ? `?${query}` : ''}`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });

            if (!response) {
                throw new Error('Failed to fetch user stats');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching user stats:', error);
            return null;
        }
    }

    async getTrends(dateRange = 'last30days') {
        try {
            const params = new URLSearchParams();
            if (dateRange) params.set('dateRange', dateRange);
            const query = params.toString();
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/trends${query ? `?${query}` : ''}`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching edit rate trends:', error);
            return [];
        }
    }

    async getDeploymentMarkers() {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/deployment-markers`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching deployment markers:', error);
            return [];
        }
    }

    async createDeploymentMarker(date, label, description) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/deployment-markers`;
            const headers = getAuthHeaders(true);
            const response = await axios.post(url, { date, label, description }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating deployment marker:', error);
            return null;
        }
    }

    async updateDeploymentMarker(id, date, label, description) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/deployment-markers/${id}`;
            const headers = getAuthHeaders(true);
            const response = await axios.put(url, { date, label, description }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error updating deployment marker:', error);
            return null;
        }
    }

    async deleteDeploymentMarker(id) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/deployment-markers/${id}`;
            const headers = getAuthHeaders(true);
            await axios.delete(url, { headers });
            return true;
        } catch (error) {
            console.error('Error deleting deployment marker:', error);
            return false;
        }
    }

    async clearTrackingData() {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/clear-all-data`;
            const headers = getAuthHeaders(true);
            const response = await axios.delete(url, { headers });

            if (!response) {
                throw new Error('Failed to clear all data');
            }
        } catch (error) {
            console.error('Error clearing data', error);
            return null;
        }
    }

    async getGlobalLeaderboard(sortBy = 'time', folderPath = null, dateRange = 'last30days', language = null) {
        try {
            console.log('getGlobalLeaderboard called with folderPath and dateRange:', folderPath, dateRange);

            let url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/leaderboard/global?sortBy=${sortBy}`;

            // Add folder filter if provided
            if (folderPath && folderPath !== '/') {
                url += `&folderPath=${encodeURIComponent(folderPath)}`;
            }

            if (dateRange) {
                url += `&dateRange=${encodeURIComponent(dateRange)}`;
            }

            if (language) {
                url += `&language=${encodeURIComponent(language)}`;
            }

            console.log('Making request to:', url);

            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });

            return response.data;
        } catch (error) {
            console.error('Error fetching global leaderboard:', error);
            return [];
        }
    }

    async getVideoEditStats(videoId) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/video/${videoId}/tracking-data`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });

            if (!response) {
                throw new Error('Failed to fetch video edit stats');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching video edit stats:', error);
            return null;
        }
    }

    async getVideoLanguageEditStats(videoId, language) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/video/${videoId}/language/${language}/tracking-data`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching video language edit stats:', error);
            return {};
        }
    }

    formatTime(milliseconds) {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = (minutes % 60).toString().padStart(2, '0');
        const formattedSeconds = (seconds % 60).toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    async sendHeartbeat() {
        if (!this.currentSession || !this.isTracking) {
            return;
        }

        const sessionId = this.currentSession.id;

        try {
            // Create a minimal heartbeat edit to register activity without actual edits
            const heartbeatEdit = {
                type: 'heartbeat',
                timestamp: new Date().toISOString(),
            };

            // Keep track of the heartbeat locally
            this.edits.push(heartbeatEdit);

            // Send the heartbeat to the server
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/sessions/${sessionId}/heartbeat`;
            const headers = getAuthHeaders(true);
            const response = await axios.post(url, {}, { headers });

            if (response.status === 200) {
                console.log('Heartbeat sent at:', new Date().toISOString());
            } else {
                console.error('Error sending heartbeat:', response);
                // Handle error response
                this.endSession();
            }
        } catch (error) {
            console.error('Error sending heartbeat:', error);

            if (error?.response?.status === 404) {
                this.stopTrackingLocally(
                    `Heartbeat session not found: ${sessionId || 'unknown session id'}`,
                    sessionId,
                );
            }
        }
    }

    async getUserVideoBreakdown(folderPath = null, videoId = null, dateRange = 'last30days') {
        try {
            const params = new URLSearchParams();
            if (folderPath && folderPath !== '/') params.set('folderPath', folderPath);
            if (videoId) params.set('videoId', videoId);
            if (dateRange) params.set('dateRange', dateRange);
            const query = params.toString();
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/current/videos${query ? `?${query}` : ''}`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching user video breakdown:', error);
            return [];
        }
    }

    async getUserLanguageBreakdown(folderPath = null, videoId = null, dateRange = 'last30days') {
        try {
            const params = new URLSearchParams();
            if (folderPath && folderPath !== '/') params.set('folderPath', folderPath);
            if (videoId) params.set('videoId', videoId);
            if (dateRange) params.set('dateRange', dateRange);
            const query = params.toString();
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/current/languages${query ? `?${query}` : ''}`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching user language breakdown:', error);
            return [];
        }
    }

    async getUserDetailedStats(userId, folderPath) {
        try {
            let url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/${userId}/detailed`;

            if (folderPath && folderPath !== '/') {
                url += `?folderPath=${encodeURIComponent(folderPath)}`;
            }

            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching user detailed stats:', error);
            return null;
        }
    }

    async getUserVideoLanguageStats(userId, videoId) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/users/${userId}/videos/${videoId}/languages`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching user video language stats:', error);
            return null;
        }
    }

    async getVideoContributors(videoId) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v1/edit-tracking/videos/${videoId}/contributors`;
            const headers = getAuthHeaders(true);
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching video contributors:', error);
            return [];
        }
    }
}

export default new EditTrackingService();
